import { useCallback, useEffect } from "react";
import {
  BOTTOM_AUTOLOAD_COOLDOWN_MS,
  BOTTOM_LOAD_THRESHOLD,
  BOTTOM_REARM_THRESHOLD,
  BOTTOM_STICK_THRESHOLD,
  BOTTOM_ZONE_EXIT_THRESHOLD,
  TOP_AUTOLOAD_COOLDOWN_MS,
  TOP_LOAD_THRESHOLD,
  TOP_ZONE_EXIT_THRESHOLD,
} from "../chatScrollConstants";

export const useChatScrollPagination = ({
  viewport,
  isFetchingInitial,
  isFetchingOlder,
  isFetchingNewer,
  runInitialMessagesFetch,
}) => {
  const {
    scrollContainerRef,
    messagesEndRef,
    topSentinelRef,
    bottomSentinelRef,
    shouldAutoScrollRef,
    topAutoLoadRef,
    bottomAutoLoadRef,
    topIntersectionLockRef,
    bottomIntersectionLockRef,
    topLoadIntentRef,
    bottomLoadIntentRef,
    topZoneStateRef,
    bottomZoneStateRef,
    userScrolledRef,
    historyBrowseActiveRef,
    forwardHistoryBrowseActiveRef,
    previousScrollTopRef,
    bottomPaginationArmedRef,
    initialScrollPendingRef,
    initialLoadCompleteRef,
    initialScrollTimersRef,
    paginationRef,
    inFlightRef,
  } = viewport;

  const resetTopPaginationGuards = useCallback(() => {
    topIntersectionLockRef.current = false;
    topLoadIntentRef.current = false;
    topZoneStateRef.current = {
      isInside: false,
      hasTriggeredForEntry: false,
    };
  }, [topIntersectionLockRef, topLoadIntentRef, topZoneStateRef]);

  const resetBottomPaginationGuards = useCallback(() => {
    bottomIntersectionLockRef.current = false;
    bottomLoadIntentRef.current = false;
    bottomZoneStateRef.current = {
      isInside: false,
      hasTriggeredForEntry: false,
    };
  }, [bottomIntersectionLockRef, bottomLoadIntentRef, bottomZoneStateRef]);

  const scrollToBottom = useCallback(({ behavior = "smooth" } = {}) => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
      previousScrollTopRef.current = Math.max(container.scrollTop, 0);
    }
    resetTopPaginationGuards();
    resetBottomPaginationGuards();
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ block: "end", behavior });
    }
  }, [messagesEndRef, previousScrollTopRef, resetBottomPaginationGuards, resetTopPaginationGuards, scrollContainerRef]);

  const flushInitialScrollTimers = useCallback(() => {
    initialScrollTimersRef.current.forEach((timerId) => clearTimeout(timerId));
    initialScrollTimersRef.current = [];
  }, [initialScrollTimersRef]);

  const scheduleInitialScrollToBottom = useCallback(() => {
    flushInitialScrollTimers();
    initialScrollPendingRef.current = true;
    initialLoadCompleteRef.current = false;
    const steps = [0, 80, 220, 450];
    steps.forEach((delay, index) => {
      const timerId = setTimeout(() => {
        scrollToBottom({ behavior: "auto" });
        if (index === steps.length - 1) {
          initialScrollPendingRef.current = false;
        }
      }, delay);
      initialScrollTimersRef.current.push(timerId);
    });
    const completionTimerId = setTimeout(() => {
      initialLoadCompleteRef.current = true;
    }, 600);
    initialScrollTimersRef.current.push(completionTimerId);
  }, [flushInitialScrollTimers, initialLoadCompleteRef, initialScrollPendingRef, initialScrollTimersRef, scrollToBottom]);

  const isUserNearBottom = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return true;
    const hiddenBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    return hiddenBottom <= BOTTOM_STICK_THRESHOLD;
  }, [scrollContainerRef]);

  const loadInitialMessages = useCallback(async () => {
    const ok = await runInitialMessagesFetch();
    if (ok) {
      shouldAutoScrollRef.current = true;
      scheduleInitialScrollToBottom();
    }
    return ok;
  }, [runInitialMessagesFetch, scheduleInitialScrollToBottom, shouldAutoScrollRef]);

  const maybeLoadOlderIfTopVisible = useCallback(() => {
    const container = scrollContainerRef.current;
    const sentinel = topSentinelRef.current;

    if (!container || !sentinel) {
      return;
    }
    if (!initialLoadCompleteRef.current) {
      return;
    }
    if (isFetchingInitial) {
      return;
    }
    if (initialScrollPendingRef.current) {
      return;
    }
    const scrollTop = Math.max(container.scrollTop, 0);
    const nearTop = scrollTop <= TOP_LOAD_THRESHOLD;
    const allowHistoryContinuation = historyBrowseActiveRef.current && nearTop;
    if (!topLoadIntentRef.current && !allowHistoryContinuation) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const sentinelRect = sentinel.getBoundingClientRect();
    const isSentinelVisible =
        sentinelRect.top <= containerRect.top + 40 &&
        sentinelRect.bottom >= containerRect.top - 20;

    if (!isSentinelVisible) {
      topIntersectionLockRef.current = false;
      return;
    }
    if (topIntersectionLockRef.current) {
      return;
    }
    const page = paginationRef.current;
    if (!page.hasPrevPage || !page.prevCursor) {
      return;
    }
    if (isFetchingOlder) {
      return;
    }
    if (inFlightRef.current.older) {
      return;
    }
    const now = Date.now();
    if (now - topAutoLoadRef.current.lastAt < TOP_AUTOLOAD_COOLDOWN_MS) {
      return;
    }

    topIntersectionLockRef.current = true;
    topZoneStateRef.current.hasTriggeredForEntry = true;
    topLoadIntentRef.current = false;
    topAutoLoadRef.current.lastAt = now;
    if (viewport.loadOlderMessagesRef.current) {
      viewport.loadOlderMessagesRef.current();
    }
  }, [
    historyBrowseActiveRef,
    initialLoadCompleteRef,
    initialScrollPendingRef,
    inFlightRef,
    isFetchingInitial,
    isFetchingOlder,
    paginationRef,
    scrollContainerRef,
    topAutoLoadRef,
    topIntersectionLockRef,
    topLoadIntentRef,
    topSentinelRef,
    topZoneStateRef,
    viewport,
  ]);

  const maybeLoadNewerIfBottomVisible = useCallback(() => {
    const container = scrollContainerRef.current;
    const sentinel = bottomSentinelRef.current;

    if (!container || !sentinel) {
      return;
    }
    if (!bottomPaginationArmedRef.current) {
      return;
    }
    if (!initialLoadCompleteRef.current) {
      return;
    }
    if (isFetchingInitial) {
      return;
    }
    if (initialScrollPendingRef.current) {
      return;
    }
    const scrollTop = Math.max(container.scrollTop, 0);
    const hiddenBottom = container.scrollHeight - scrollTop - container.clientHeight;
    const nearBottom = hiddenBottom <= BOTTOM_LOAD_THRESHOLD;
    const allowForwardContinuation = forwardHistoryBrowseActiveRef.current && nearBottom;
    if (!bottomLoadIntentRef.current && !allowForwardContinuation) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const sentinelRect = sentinel.getBoundingClientRect();
    const isSentinelVisible =
        sentinelRect.bottom >= containerRect.bottom - 40 &&
        sentinelRect.top <= containerRect.bottom + 20;

    if (!isSentinelVisible) {
      bottomIntersectionLockRef.current = false;
      return;
    }
    if (bottomIntersectionLockRef.current) {
      return;
    }
    const page = paginationRef.current;
    if (!page.hasNextPage || !page.nextCursor) {
      return;
    }
    if (isFetchingNewer) {
      return;
    }
    if (inFlightRef.current.newer) {
      return;
    }
    const now = Date.now();
    if (now - bottomAutoLoadRef.current.lastAt < BOTTOM_AUTOLOAD_COOLDOWN_MS) {
      return;
    }

    bottomIntersectionLockRef.current = true;
    bottomZoneStateRef.current.hasTriggeredForEntry = true;
    bottomLoadIntentRef.current = false;
    bottomAutoLoadRef.current.lastAt = now;
    if (viewport.loadNewerMessagesRef.current) {
      viewport.loadNewerMessagesRef.current();
    }
  }, [
    bottomAutoLoadRef,
    bottomIntersectionLockRef,
    bottomLoadIntentRef,
    bottomPaginationArmedRef,
    bottomSentinelRef,
    bottomZoneStateRef,
    forwardHistoryBrowseActiveRef,
    initialLoadCompleteRef,
    initialScrollPendingRef,
    inFlightRef,
    isFetchingInitial,
    isFetchingNewer,
    paginationRef,
    scrollContainerRef,
    viewport,
  ]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return undefined;
    previousScrollTopRef.current = container.scrollTop;
    const onScroll = () => {
      const prevTop = previousScrollTopRef.current;
      const rawTop = container.scrollTop;
      const nextTop = Math.max(rawTop, 0);
      const delta = nextTop - prevTop;
      if (Math.abs(delta) > 1) {
        userScrolledRef.current = true;
      }
      if (delta < -1) {
        historyBrowseActiveRef.current = true;
      }
      previousScrollTopRef.current = nextTop;
      shouldAutoScrollRef.current = isUserNearBottom();
      const isScrollingUp = delta < -1;
      const isScrollingDown = delta > 1;
      if (isScrollingDown) {
        forwardHistoryBrowseActiveRef.current = true;
      }
      if (nextTop <= TOP_LOAD_THRESHOLD) {
        if (!topZoneStateRef.current.isInside) {
          topZoneStateRef.current.isInside = true;
          topZoneStateRef.current.hasTriggeredForEntry = false;
        }
        if ((isScrollingUp || rawTop <= 0) && !isFetchingOlder && !inFlightRef.current.older) {
          topIntersectionLockRef.current = false;
          topLoadIntentRef.current = true;
          maybeLoadOlderIfTopVisible();
        }
      } else if (nextTop > TOP_ZONE_EXIT_THRESHOLD) {
        topZoneStateRef.current.isInside = false;
        topZoneStateRef.current.hasTriggeredForEntry = false;
        topLoadIntentRef.current = false;
        topIntersectionLockRef.current = false;
      }
      const hiddenBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      if (!bottomPaginationArmedRef.current) {
        if (isScrollingDown && hiddenBottom <= BOTTOM_REARM_THRESHOLD) {
          bottomPaginationArmedRef.current = true;
        } else {
          return;
        }
      }
      const maxTop = Math.max(container.scrollHeight - container.clientHeight, 0);
      if (hiddenBottom <= BOTTOM_LOAD_THRESHOLD) {
        if (!bottomZoneStateRef.current.isInside) {
          bottomZoneStateRef.current.isInside = true;
          bottomZoneStateRef.current.hasTriggeredForEntry = false;
        }
        if (
            (isScrollingDown || hiddenBottom <= 0 || rawTop >= maxTop - 1) &&
            !isFetchingNewer &&
            !inFlightRef.current.newer
        ) {
          bottomIntersectionLockRef.current = false;
          bottomLoadIntentRef.current = true;
          maybeLoadNewerIfBottomVisible();
        }
      } else if (hiddenBottom > BOTTOM_ZONE_EXIT_THRESHOLD) {
        bottomZoneStateRef.current.isInside = false;
        bottomZoneStateRef.current.hasTriggeredForEntry = false;
        bottomLoadIntentRef.current = false;
        bottomIntersectionLockRef.current = false;
      }
    };
    container.addEventListener("scroll", onScroll);
    return () => {
      container.removeEventListener("scroll", onScroll);
    };
  }, [
    bottomIntersectionLockRef,
    bottomLoadIntentRef,
    bottomPaginationArmedRef,
    bottomZoneStateRef,
    forwardHistoryBrowseActiveRef,
    historyBrowseActiveRef,
    inFlightRef,
    isFetchingNewer,
    isFetchingOlder,
    isUserNearBottom,
    maybeLoadNewerIfBottomVisible,
    maybeLoadOlderIfTopVisible,
    previousScrollTopRef,
    scrollContainerRef,
    shouldAutoScrollRef,
    topIntersectionLockRef,
    topLoadIntentRef,
    topZoneStateRef,
    userScrolledRef,
  ]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    const sentinel = topSentinelRef.current;
    if (!container || !sentinel) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((entry) => entry.isIntersecting);
        if (!visible) {
          topIntersectionLockRef.current = false;
          return;
        }
        maybeLoadOlderIfTopVisible();
      },
      { root: container, threshold: [0, 0.01, 1] }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [maybeLoadOlderIfTopVisible, scrollContainerRef, topIntersectionLockRef, topSentinelRef]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    const sentinel = bottomSentinelRef.current;
    if (!container || !sentinel) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((entry) => entry.isIntersecting);
        if (!visible) {
          bottomIntersectionLockRef.current = false;
          return;
        }
        maybeLoadNewerIfBottomVisible();
      },
      { root: container, threshold: [0, 0.01, 1] }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [bottomIntersectionLockRef, bottomSentinelRef, maybeLoadNewerIfBottomVisible, scrollContainerRef]);

  useEffect(() => {
    if (!isFetchingOlder) {
      const timerId = setTimeout(() => {
        const container = scrollContainerRef.current;
        if (!container) return;
        const nextTop = Math.max(container.scrollTop, 0);
        if (nextTop <= TOP_LOAD_THRESHOLD && paginationRef.current.hasPrevPage) {
          topZoneStateRef.current.isInside = true;
          topZoneStateRef.current.hasTriggeredForEntry = false;
          topIntersectionLockRef.current = false;
          return;
        }
        if (nextTop > TOP_ZONE_EXIT_THRESHOLD) {
          topZoneStateRef.current.isInside = false;
          topZoneStateRef.current.hasTriggeredForEntry = false;
          topLoadIntentRef.current = false;
          topIntersectionLockRef.current = false;
        }
      }, 80);
      return () => clearTimeout(timerId);
    }
    return undefined;
  }, [isFetchingOlder, paginationRef, scrollContainerRef, topIntersectionLockRef, topLoadIntentRef, topZoneStateRef]);

  useEffect(() => {
    if (!isFetchingNewer) {
      const timerId = setTimeout(() => {
        const container = scrollContainerRef.current;
        if (!container) return;
        const scrollTop = Math.max(container.scrollTop, 0);
        const hiddenBottom = container.scrollHeight - scrollTop - container.clientHeight;
        const nextPage = paginationRef.current;
        if (hiddenBottom <= BOTTOM_LOAD_THRESHOLD && nextPage.hasNextPage) {
          bottomZoneStateRef.current.isInside = true;
          bottomZoneStateRef.current.hasTriggeredForEntry = false;
          bottomIntersectionLockRef.current = false;
          return;
        }
        if (hiddenBottom > BOTTOM_ZONE_EXIT_THRESHOLD) {
          bottomZoneStateRef.current.isInside = false;
          bottomZoneStateRef.current.hasTriggeredForEntry = false;
          bottomLoadIntentRef.current = false;
          bottomIntersectionLockRef.current = false;
        }
      }, 80);
      return () => clearTimeout(timerId);
    }
    return undefined;
  }, [
    bottomIntersectionLockRef,
    bottomLoadIntentRef,
    bottomZoneStateRef,
    isFetchingNewer,
    paginationRef,
    scrollContainerRef,
  ]);

  useEffect(() => () => flushInitialScrollTimers(), [flushInitialScrollTimers]);

  const resetViewportForNewChatId = useCallback(() => {
    userScrolledRef.current = false;
    historyBrowseActiveRef.current = false;
    forwardHistoryBrowseActiveRef.current = false;
    previousScrollTopRef.current = 0;
    bottomPaginationArmedRef.current = true;
    initialLoadCompleteRef.current = false;
    topAutoLoadRef.current = { lastAt: 0 };
    bottomAutoLoadRef.current = { lastAt: 0 };
    resetTopPaginationGuards();
    resetBottomPaginationGuards();
    viewport.readStateRef.current.lastMarkedChatId = null;
    viewport.lastSeenIncomingMessageIdRef.current = null;
    flushInitialScrollTimers();
    initialScrollPendingRef.current = true;
  }, [
    bottomAutoLoadRef,
    bottomPaginationArmedRef,
    flushInitialScrollTimers,
    forwardHistoryBrowseActiveRef,
    historyBrowseActiveRef,
    initialLoadCompleteRef,
    initialScrollPendingRef,
    previousScrollTopRef,
    resetBottomPaginationGuards,
    resetTopPaginationGuards,
    topAutoLoadRef,
    userScrolledRef,
    viewport,
  ]);

  return {
    scrollToBottom,
    flushInitialScrollTimers,
    scheduleInitialScrollToBottom,
    isUserNearBottom,
    loadInitialMessages,
    maybeLoadOlderIfTopVisible,
    maybeLoadNewerIfBottomVisible,
    resetTopPaginationGuards,
    resetBottomPaginationGuards,
    resetViewportForNewChatId,
  };
};
